import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Loader2, PanelRightOpen } from 'lucide-react'

import { useIsMobile } from '@/common/hooks/useMobile'
import { Button } from '@/common/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/common/ui/tooltip'

import { useChatSse } from '../../sse'
import { toast } from 'sonner'
import {
	useQueryChatRoom,
	useMutationClaimRoom,
	useMutationReleaseRoom,
	useMutationPinMessage,
	useMutationPinRoom,
	useMutationBanRoom,
	useMutationUpdateRoomStatus,
	useMutationSendMultipleMessages,
	useMutationMarkRoomAsRead,
	useMutationLinkPlayer,
	useQueryAiDraft,
	useMutationAiDraftUse,
	useMutationAiDraftEdit,
	useMutationAiDraftIgnore,
	useAiGlobalEnabled,
} from '../../api/hooks'
import {
	MessageBubble,
	type LinkPlayerFromMessagePayload,
} from '../MessageBubble'
import { QuickResponsesPanel } from '../QuickResponses'
import { StickerPicker } from '../StickerPicker'
import { MediaAttachmentPicker, type MediaType } from '../MediaAttachmentPicker'
import { TagManagerPanel } from '../TagManager'
import { LinkPlayerDialog } from '../LinkPlayerDialog'
import { SearchMessagesDialog } from '../SearchMessagesDialog'
import { PinnedMessagesBanner } from '../PinnedMessagesBanner'
import { ProfileActionDialog } from '../ProfileActionDialog'
import { BanRoomDialog } from '../BanRoomDialog'
import { InstantCreditClaimDialog } from '../InstantCreditClaimDialog'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { useMutationConnectLine } from '../../api/hooks'

import {
	useChatMessages,
	useAutoScrollChat,
	useSendMessage,
	useNavigateToMessage,
} from './hooks'
import { groupMessagesByDate } from './utils'
import {
	ChatRoomHeader,
	ChatInput,
	ScrollToBottomButton,
	ChatRoomSkeleton,
	MessageSkeleton,
	ChatNotesPanel,
	ChatNotesMobileSheet,
	ChatAdminTasksPanel,
	ChatAdminTasksMobileSheet,
	AiDraftSuggestion,
} from './components'

import { ChatMessageType, ChatRoomStatus, type components } from '@/schema'

type ChatMessageResponse = components['schemas']['ChatMessageResponse']
type ChatMessageContentDto = components['schemas']['ChatMessageContentDto']
type QuickResponseResponse = components['schemas']['QuickResponseResponse']

// ============================================================================
// ChatRoom Component
// ============================================================================

interface ChatRoomProps {
	roomId: string
}

export function ChatRoom({ roomId }: ChatRoomProps) {
	const isMobile = useIsMobile()
	const [input, setInput] = useState('')
	const [activeDraftId, setActiveDraftId] = useState<string | null>(null)
	const [showQuickResponses, setShowQuickResponses] = useState(false)
	const [showStickerPicker, setShowStickerPicker] = useState(false)
	const [showMediaPicker, setShowMediaPicker] = useState(false)
	const [showInstantCreditClaim, setShowInstantCreditClaim] = useState(false)
	const [showTagManager, setShowTagManager] = useState(false)
	const [showLinkPlayer, setShowLinkPlayer] = useState(false)
	const [showSearchDialog, setShowSearchDialog] = useState(false)
	const [showPinnedBanner, setShowPinnedBanner] = useState(true)
	const [showProfileDialog, setShowProfileDialog] = useState(false)
	const [showBanDialog, setShowBanDialog] = useState(false)
	const [showNotesDialog, setShowNotesDialog] = useState(false)
	const [showNotesPanel, setShowNotesPanel] = useState(false)
	const [showAdminTasksPanel, setShowAdminTasksPanel] = useState(false)
	const [showAdminTasksSheet, setShowAdminTasksSheet] = useState(false)
	const [replyToMessage, setReplyToMessage] =
		useState<ChatMessageResponse | null>(null)
	const [pastedFile, setPastedFile] = useState<File | null>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)

	const { setCurrentRoomId } = useChatSse()
	const { data: room, isLoading: roomLoading } = useQueryChatRoom(roomId)
	const { data: profile } = useQueryProfile()
	const { aiEnabled } = useAiGlobalEnabled()
	const [linkingPlayerMessageId, setLinkingPlayerMessageId] = useState<
		string | null
	>(null)
	const linkPlayerFromMessage = useMutationLinkPlayer(roomId)

	const handleLinkPlayerFromMessage = useCallback(
		async (messageId: string, payload: LinkPlayerFromMessagePayload) => {
			setLinkingPlayerMessageId(messageId)
			try {
				await linkPlayerFromMessage.mutateAsync(
					'playerId' in payload
						? { playerId: payload.playerId }
						: { phone: payload.phone },
				)
				toast.success('เชื่อมต่อผู้เล่นสำเร็จ')
			} catch {
				toast.error('เชื่อมต่อล้มเหลว กรุณาตรวจสอบเบอร์โทรหรือชื่อผู้ใช้')
			} finally {
				setLinkingPlayerMessageId(null)
			}
		},
		[linkPlayerFromMessage],
	)

	useEffect(() => {
		if (!isMobile) {
			setShowNotesPanel(true)
			setShowNotesDialog(true)
		} else {
			setShowNotesDialog(false)
			setShowNotesPanel(false)
		}
	}, [isMobile])

	// Custom hooks
	const {
		messages,
		messagesLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		fetchPreviousPage,
		hasPreviousPage,
		isFetchingPreviousPage,
		addPendingMessageWithReset,
		markPendingAsFailed,
		removeFailedMessage,
		retryMessage,
		getPendingInfo,
		getFailedMessageData,
		jumpToMessage,
		resetToLatest,
		startCursor,
		isFetched,
	} = useChatMessages(roomId)

	const {
		messagesEndRef,
		messagesContainerRef,
		showScrollToBottom,
		newMessageCount,
		lastNewMessagePreview,
		highlightedMessageId,
		scrollToBottom,
		forceScrollToBottom,
		updateScrollIndicator,
		scrollToExistingMessage,
	} = useAutoScrollChat({
		roomId,
		messages,
		messagesLoading,
		isFetched,
		startCursor,
		hasPreviousPage,
		resetToLatest,
	})

	// Navigate to message hook
	const { navigateToMessage } = useNavigateToMessage({
		messages,
		jumpToMessage,
		scrollToExistingMessage,
	})

	const {
		sendTextMessage,
		sendQuickResponse,
		sendSticker,
		sendMedia,
		sendCustomMessage,
		isUploadingMedia,
	} = useSendMessage({
		roomId,
		addPendingMessageWithReset,
		markPendingAsFailed,
		forceScrollToBottom,
	})

	// Mutations
	const claimRoom = useMutationClaimRoom()
	const releaseRoom = useMutationReleaseRoom()
	const pinMessage = useMutationPinMessage(roomId)
	const pinRoom = useMutationPinRoom()
	const banRoom = useMutationBanRoom()
	const updateRoomStatus = useMutationUpdateRoomStatus()
	const sendMultipleMessagesMutation = useMutationSendMultipleMessages(roomId)
	const markAsRead = useMutationMarkRoomAsRead()
	const connectLine = useMutationConnectLine(roomId)

	const markDraftUsed = useMutationAiDraftUse(roomId)
	const markDraftEdited = useMutationAiDraftEdit(roomId)
	const markDraftIgnored = useMutationAiDraftIgnore(roomId)
	const { data: currentDraft } = useQueryAiDraft(roomId)

	// Track current room for SSE context
	useEffect(() => {
		if (roomId) {
			setCurrentRoomId(roomId)
			return () => setCurrentRoomId(null)
		}
	}, [roomId, setCurrentRoomId])

	// Handle scroll for loading older/newer messages
	const handleScroll = useCallback(() => {
		const container = messagesContainerRef.current
		if (!container) return

		updateScrollIndicator()

		// Load older messages when scrolled near top
		if (!isFetchingNextPage && hasNextPage && container.scrollTop < 100) {
			const previousScrollHeight = container.scrollHeight
			fetchNextPage().then(() => {
				// Maintain scroll position after loading older messages
				requestAnimationFrame(() => {
					if (container) {
						const newScrollHeight = container.scrollHeight
						container.scrollTop = newScrollHeight - previousScrollHeight
					}
				})
			})
		}

		// Load newer messages when scrolled near bottom
		const isNearBottom =
			container.scrollHeight - container.scrollTop - container.clientHeight <
			100
		if (!isFetchingPreviousPage && hasPreviousPage && isNearBottom) {
			fetchPreviousPage()
		}
	}, [
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		fetchPreviousPage,
		hasPreviousPage,
		isFetchingPreviousPage,
		updateScrollIndicator,
		messagesContainerRef,
	])

	useEffect(() => {
		const container = messagesContainerRef.current
		if (!container) return

		container.addEventListener('scroll', handleScroll)
		return () => container.removeEventListener('scroll', handleScroll)
	}, [handleScroll, messagesContainerRef])

	// Handlers
	const handleSend = useCallback(() => {
		const text = input.trim()
		if (!text) return

		sendTextMessage(text, replyToMessage?.id)
		setInput('')
		setReplyToMessage(null)
		textareaRef.current?.focus()
		forceScrollToBottom()

		if (activeDraftId) {
			const isEdited = text !== (currentDraft?.draftText ?? null)
			if (isEdited) {
				markDraftEdited.mutate({ draftId: activeDraftId, finalText: text })
			} else {
				markDraftUsed.mutate({ draftId: activeDraftId })
			}
			setActiveDraftId(null)
		}
	}, [
		input,
		sendTextMessage,
		replyToMessage,
		forceScrollToBottom,
		activeDraftId,
		currentDraft,
		markDraftUsed,
		markDraftEdited,
	])

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault()
				handleSend()
			}
		},
		[handleSend],
	)

	const handleQuickResponse = useCallback(
		(response: QuickResponseResponse) => {
			sendQuickResponse(response)
			setShowQuickResponses(false)
		},
		[sendQuickResponse],
	)

	const handleStickerSelect = useCallback(
		(sticker: { packageId: string; stickerId: string }) => {
			sendSticker(sticker)
			setShowStickerPicker(false)
		},
		[sendSticker],
	)

	const handleMediaSelect = useCallback(
		async (file: File, mediaType: MediaType) => {
			await sendMedia(file, mediaType)
			setShowMediaPicker(false)
			setPastedFile(null)
		},
		[sendMedia],
	)

	const handlePasteFile = useCallback((file: File) => {
		setPastedFile(file)
		setShowMediaPicker(true)
		setShowQuickResponses(false)
		setShowStickerPicker(false)
	}, [])

	const handleClaim = useCallback(() => {
		claimRoom.mutate(roomId)
	}, [claimRoom, roomId])

	const handleRelease = useCallback(() => {
		releaseRoom.mutate(roomId)
	}, [releaseRoom, roomId])

	const handlePinMessage = useCallback(
		(messageId: string, isPinned: boolean) => {
			pinMessage.mutate({ messageId, isPinned })
		},
		[pinMessage],
	)

	const handlePinRoom = useCallback(
		(isPinned: boolean) => {
			pinRoom.mutate({ roomId, isPinned })
		},
		[pinRoom, roomId],
	)

	const handleOpenBanDialog = useCallback(() => {
		setShowBanDialog(true)
	}, [])

	const handleBanRoom = useCallback(
		(isBanned: boolean, reason?: string) => {
			banRoom.mutate(
				{ roomId, isBanned, reason },
				{
					onSuccess: () => {
						setShowBanDialog(false)
					},
				},
			)
		},
		[banRoom, roomId],
	)

	const handleConfirmBan = useCallback(
		(reason: string) => {
			handleBanRoom(true, reason)
		},
		[handleBanRoom],
	)

	const handleMarkAsRead = useCallback(() => {
		markAsRead.mutate(roomId)
	}, [markAsRead, roomId])

	const handleUpdateStatus = useCallback(
		(status: components['schemas']['UpdateChatRoomStatusDto']['status']) => {
			updateRoomStatus.mutate({ roomId, status })
		},
		[updateRoomStatus, roomId],
	)

	const handleReplyToMessage = useCallback((message: ChatMessageResponse) => {
		setReplyToMessage(message)
		textareaRef.current?.focus()
	}, [])

	const handleCancelReply = useCallback(() => {
		setReplyToMessage(null)
	}, [])

	const handleUseDraft = useCallback(() => {
		const draft = currentDraft
		if (!draft || !draft.draftText) return
		setInput(draft.draftText)
		setActiveDraftId(draft.draftId)
	}, [currentDraft])

	const handleIgnoreDraft = useCallback(() => {
		const draft = currentDraft
		if (!draft) return
		markDraftIgnored.mutate(draft.draftId)
		setActiveDraftId(null)
	}, [currentDraft, markDraftIgnored])

	// Handle retry failed message
	const handleRetryMessage = useCallback(
		(clientId: string) => {
			const failedMessage = getFailedMessageData(clientId)
			if (!failedMessage) return

			// Reset status to pending
			retryMessage(clientId)

			// Build the message data from the failed message
			const msg = failedMessage.message
			const messageData: ChatMessageContentDto = {
				clientId,
				messageType: msg.messageType as ChatMessageType,
				order: 0,
				text: msg.text ?? undefined,
				stickerPackageId: msg.stickerPackageId ?? undefined,
				stickerId: msg.stickerId ?? undefined,
				imageUrl: msg.imageOriginalUrl ?? undefined,
			}

			// Resend the message using multiple messages API
			sendMultipleMessagesMutation.mutate(
				{ messages: [messageData] },
				{
					onError: () => markPendingAsFailed(clientId),
				},
			)
		},
		[
			getFailedMessageData,
			retryMessage,
			sendMultipleMessagesMutation,
			markPendingAsFailed,
		],
	)

	// Handle remove failed message
	const handleRemoveMessage = useCallback(
		(clientId: string) => {
			removeFailedMessage(clientId)
		},
		[removeFailedMessage],
	)

	// Handle navigation to message from search or pinned banner
	const handleNavigateToMessage = useCallback(
		(messageId: string) => {
			// For now, we only support navigation within the current room
			// If messageRoomId is different from current roomId, we'd need to change rooms first
			navigateToMessage(messageId)
		},
		[navigateToMessage],
	)

	// Handle go to player detail
	const handleGoToPlayer = useCallback(() => {
		if (room?.player) {
			window.open(
				`/operator/get-player-list/player-detail/${room.player.id}`,
				'_blank',
			)
		}
	}, [room?.player])

	// Handle toggle notes panel - desktop uses panel, mobile uses sheet
	const handleToggleNotesPanel = useCallback(() => {
		if (isMobile) {
			setShowNotesDialog((prev) => !prev)
		} else {
			setShowNotesPanel((prev) => !prev)
			setShowAdminTasksPanel(false)
		}
	}, [isMobile])

	const handleToggleAdminTasks = useCallback(() => {
		if (isMobile) {
			setShowAdminTasksSheet((prev) => !prev)
		} else {
			setShowAdminTasksPanel((prev) => !prev)
			setShowNotesPanel(false)
		}
	}, [isMobile])

	// Handle connect LINE
	const handleConnectLine = useCallback(() => {
		connectLine.mutate()
	}, [connectLine])

	// Check if current user is the assigned operator
	const isMyChat = room?.assignedOperator?.id === profile?.id

	// Group messages by date
	const groupedMessages = useMemo(
		() => groupMessagesByDate(messages),
		[messages],
	)

	// Loading state
	if (roomLoading) {
		return <ChatRoomSkeleton />
	}

	if (!room) {
		return (
			<div className="flex h-full items-center justify-center text-muted-foreground">
				ไม่พบห้องแชท
			</div>
		)
	}

	const displayName = room.lineDisplayName || room.lineUserId

	const draft = currentDraft

	return (
		<div className="flex h-full">
			{/* Main Chat Area */}
			<div className="flex min-w-0 flex-1 flex-col">
				{/* Header */}
				<ChatRoomHeader
					room={room}
					displayName={displayName || ''}
					onClaim={handleClaim}
					onRelease={handleRelease}
					onOpenTagManager={() => setShowTagManager(true)}
					onOpenLinkPlayer={() => setShowLinkPlayer(true)}
					onOpenSearch={() => setShowSearchDialog(true)}
					onPinRoom={handlePinRoom}
					onOpenBanDialog={handleOpenBanDialog}
					onUnbanRoom={() => handleBanRoom(false)}
					onUpdateStatus={handleUpdateStatus}
					onMarkAsRead={handleMarkAsRead}
					isMyChat={isMyChat}
					onOpenProfile={() => setShowProfileDialog(true)}
					onGoToPlayer={handleGoToPlayer}
					onOpenInstantCredit={() => setShowInstantCreditClaim(true)}
					onToggleAdminTasks={handleToggleAdminTasks}
				/>

				{/* Pinned Messages Banner */}
				{showPinnedBanner && (
					<PinnedMessagesBanner
						roomId={roomId}
						onClose={() => setShowPinnedBanner(false)}
						avatarUrl={room.linePictureUrl}
						avatarName={displayName}
						onNavigateToMessage={handleNavigateToMessage}
					/>
				)}

				{/* Messages */}
				<div className="relative flex-1 overflow-hidden">
					<div
						ref={messagesContainerRef}
						className="h-full overflow-y-auto p-4"
					>
						{messagesLoading ? (
							<div className="space-y-4">
								{Array.from({ length: 5 }).map((_, i) => (
									<MessageSkeleton key={i} isOutgoing={i % 2 === 0} />
								))}
							</div>
						) : messages.length === 0 ? (
							<div className="flex h-full items-center justify-center text-muted-foreground">
								ยังไม่มีข้อความ
							</div>
						) : (
							<div className="space-y-2">
								{/* Load more indicator */}
								{isFetchingNextPage && (
									<div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
										<Loader2 className="h-4 w-4 animate-spin" />
										กำลังโหลดข้อความเก่า...
									</div>
								)}

								{/* End of history indicator */}
								{!hasNextPage && messages.length > 0 && (
									<div className="py-2 text-center text-xs text-muted-foreground">
										จุดเริ่มต้นของการสนทนา
									</div>
								)}

								{groupedMessages.map((group) => (
									<div key={group.date}>
										{/* Date Divider */}
										<div className="my-2 flex items-center justify-center">
											<span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
												{group.date}
											</span>
										</div>

										{/* Messages */}
										<div className="space-y-2">
											{group.messages.map((msg) => {
												const pendingInfo = getPendingInfo(msg.clientId)

												return (
													<MessageBubble
														key={msg.id}
														message={msg}
														avatarUrl={room.linePictureUrl}
														avatarName={displayName}
														isPending={pendingInfo?.status === 'pending'}
														isFailed={pendingInfo?.status === 'failed'}
														isHighlighted={highlightedMessageId === msg.id}
														onPinMessage={handlePinMessage}
														onReplyMessage={handleReplyToMessage}
														onRetryMessage={handleRetryMessage}
														onRemoveMessage={handleRemoveMessage}
														onNavigateToMessage={handleNavigateToMessage}
														onAvatarClick={() => setShowProfileDialog(true)}
														roomId={roomId}
														enableLinkPlayerFromMessage={!room.player}
														onLinkPlayerFromMessage={(payload) =>
															handleLinkPlayerFromMessage(msg.id, payload)
														}
														isLinkingPlayerFromMessage={
															linkingPlayerMessageId === msg.id
														}
													/>
												)
											})}
										</div>
									</div>
								))}
								<div ref={messagesEndRef} />
							</div>
						)}
					</div>

					{/* Scroll to bottom button */}
					{showScrollToBottom && (
						<ScrollToBottomButton
							newMessageCount={newMessageCount}
							messagePreview={lastNewMessagePreview}
							onClick={() => scrollToBottom('smooth')}
						/>
					)}

					{/* Notes panel toggle button */}
					{!showNotesPanel && (
						<div className="absolute right-0 top-4 z-10">
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="outline"
										size="icon"
										className="h-9 w-9 rounded-r-none border-r-0 bg-background/95 shadow-sm backdrop-blur"
										onClick={handleToggleNotesPanel}
									>
										<PanelRightOpen className="h-4 w-4" />
									</Button>
								</TooltipTrigger>
								<TooltipContent side="right">
									<p>เปิดโน้ต</p>
								</TooltipContent>
							</Tooltip>
						</div>
					)}
				</div>

				{/* Panels */}
				{showQuickResponses && (
					<QuickResponsesPanel
						onSelect={handleQuickResponse}
						onClose={() => setShowQuickResponses(false)}
					/>
				)}

				{showStickerPicker && (
					<StickerPicker
						onSelect={handleStickerSelect}
						onClose={() => setShowStickerPicker(false)}
					/>
				)}

				{showMediaPicker && (
					<MediaAttachmentPicker
						onMediaSelect={handleMediaSelect}
						onClose={() => {
							setShowMediaPicker(false)
							setPastedFile(null)
						}}
						isUploading={isUploadingMedia}
						initialFile={pastedFile}
					/>
				)}

				{showTagManager && (
					<TagManagerPanel
						roomId={roomId}
						currentTags={room.tags}
						playerTags={room.player?.tags}
						onClose={() => setShowTagManager(false)}
					/>
				)}

				<LinkPlayerDialog
					roomId={roomId}
					currentPlayer={room.player}
					open={showLinkPlayer}
					onOpenChange={setShowLinkPlayer}
				/>

				{/* Profile Action Dialog */}
				<ProfileActionDialog
					open={showProfileDialog}
					onOpenChange={setShowProfileDialog}
					displayName={displayName || ''}
					pictureUrl={room.linePictureUrl}
					player={room.player}
					onLinkPlayer={() => setShowLinkPlayer(true)}
					onConnectLine={handleConnectLine}
					isConnectLineLoading={connectLine.isPending}
					lineUserId={room.lineUserId}
				/>

				{/* Search Dialog */}
				<SearchMessagesDialog
					open={showSearchDialog}
					onOpenChange={setShowSearchDialog}
					roomId={roomId}
					onNavigateToMessage={handleNavigateToMessage}
				/>

				{/* Ban Room Dialog */}
				<BanRoomDialog
					open={showBanDialog}
					onOpenChange={setShowBanDialog}
					onConfirm={handleConfirmBan}
					displayName={displayName || ''}
					isLoading={banRoom.isPending}
				/>

				{/* Instant Credit Claim Dialog */}
				<InstantCreditClaimDialog
					open={showInstantCreditClaim}
					onOpenChange={setShowInstantCreditClaim}
					onSendMessage={sendCustomMessage}
				/>

				{aiEnabled &&
				draft &&
				draft.action !== 'drop' &&
				draft.action !== 'escalate' &&
				room.aiAnswerMode !== 'AUTO' &&
				room.aiAnswerMode !== 'OFF' ? (
					<AiDraftSuggestion
						draft={draft}
						onUse={handleUseDraft}
						onIgnore={handleIgnoreDraft}
						aiAnswerMode={room.aiAnswerMode}
					/>
				) : null}

				{/* Input */}
				<ChatInput
					input={input}
					onInputChange={setInput}
					onSend={handleSend}
					onKeyDown={handleKeyDown}
					ref={textareaRef}
					showQuickResponses={showQuickResponses}
					showStickerPicker={showStickerPicker}
					showMediaPicker={showMediaPicker}
					onToggleQuickResponses={() => {
						setShowQuickResponses(!showQuickResponses)
						setShowStickerPicker(false)
						setShowMediaPicker(false)
					}}
					onToggleStickerPicker={() => {
						setShowStickerPicker(!showStickerPicker)
						setShowQuickResponses(false)
						setShowMediaPicker(false)
					}}
					onToggleMediaPicker={() => {
						setShowMediaPicker(!showMediaPicker)
						setShowQuickResponses(false)
						setShowStickerPicker(false)
						setPastedFile(null)
					}}
					onToggleInstantCreditClaim={() => setShowInstantCreditClaim(true)}
					replyToMessage={replyToMessage}
					onCancelReply={handleCancelReply}
					isBanned={room.isBanned}
					onUnban={() => handleBanRoom(false)}
					isClosed={room.status === ChatRoomStatus.CLOSED}
					onPasteFile={handlePasteFile}
				/>
			</div>

			{/* Notes Panel - Desktop only */}
			{!isMobile && showNotesPanel && (
				<ChatNotesPanel
					roomId={roomId}
					onClose={() => setShowNotesPanel(false)}
				/>
			)}

			{/* Admin Tasks Panel - Desktop only */}
			{aiEnabled && !isMobile && showAdminTasksPanel && (
				<ChatAdminTasksPanel
					roomId={roomId}
					onClose={() => setShowAdminTasksPanel(false)}
				/>
			)}

			{/* Notes Mobile Sheet */}
			{isMobile && (
				<ChatNotesMobileSheet
					open={showNotesDialog}
					onOpenChange={setShowNotesDialog}
					roomId={roomId}
				/>
			)}

			{/* Admin Tasks Mobile Sheet */}
			{aiEnabled && isMobile && (
				<ChatAdminTasksMobileSheet
					open={showAdminTasksSheet}
					onOpenChange={setShowAdminTasksSheet}
					roomId={roomId}
				/>
			)}
		</div>
	)
}

// Re-export ChatRoomEmpty for backward compatibility
export { ChatRoomEmpty } from './components'
